import React, { useState, useEffect } from 'react';
import {
  X, Cloud, ChevronRight, ChevronLeft, CheckCircle, AlertTriangle,
  Loader, Shield, Key, Globe, Server, Lock, Eye, EyeOff, Info,
  Database, Cpu, HardDrive, Network
} from 'lucide-react';
import { cloudScan, scans as scansApi } from '../../api/client';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const PROVIDERS = [
  {
    id: 'aws',
    name: 'Amazon Web Services',
    icon: '☁️',
    badge: 'Prowler',
    color: 'from-orange-900/40 to-orange-800/20 border-orange-500/40',
    activeColor: 'from-orange-500/30 to-orange-400/10 border-orange-400',
    description: 'IAM, S3, EC2, RDS, Lambda, CloudTrail...',
    tool: 'Prowler (open-source AWS security leader)',
    duration: '5 – 15 min',
    checks: [
      { icon: '🔑', label: 'IAM', detail: 'Policies, MFA root, unused access, excessive permissions' },
      { icon: '🪣', label: 'S3', detail: 'ACLs publiques, chiffrement au repos, versioning, logging' },
      { icon: '🖥️', label: 'EC2', detail: 'Security groups permissifs, snapshots publiques, SSM' },
      { icon: '🗃️', label: 'RDS', detail: 'Exposition publique, chiffrement, backups automatiques' },
      { icon: '⚡', label: 'Lambda', detail: 'Permissions IAM, variables d\'env sensibles, VPC' },
      { icon: '📋', label: 'CloudTrail', detail: 'Multi-region activation, log integrity, protected S3' },
      { icon: '🔐', label: 'KMS', detail: 'Key rotation, access policies, unused keys' },
      { icon: '🛡️', label: 'VPC', detail: 'Flow logs, NACLs, exposed public subnets' },
    ],
    frameworks: ['CIS AWS Benchmark 2.0', 'NIST 800-53 Rev5', 'ISO 27001:2013', 'AWS Well-Architected'],
  },
  {
    id: 'azure',
    name: 'Microsoft Azure',
    icon: '🔷',
    badge: 'Prowler',
    color: 'from-blue-900/40 to-blue-800/20 border-blue-500/40',
    activeColor: 'from-blue-500/30 to-blue-400/10 border-blue-400',
    description: 'RBAC, Storage, VMs, AKS, Key Vault...',
    tool: 'Prowler (multi-cloud security audit)',
    duration: '5 – 15 min',
    checks: [
      { icon: '👤', label: 'RBAC', detail: 'Over-elevated roles, Owner/Contributor without MFA, guest access' },
      { icon: '💾', label: 'Storage', detail: 'Public blob access, encryption, expired SAS keys' },
      { icon: '🖥️', label: 'VMs', detail: 'Unencrypted disks, permissive NSGs, JIT access' },
      { icon: '⎈', label: 'AKS', detail: 'Kubernetes RBAC, networking, pod policies' },
      { icon: '🗝️', label: 'Key Vault', detail: 'Network access, secret rotation, expired certificates' },
      { icon: '🔐', label: 'Azure AD', detail: 'MFA, conditional access, legacy authentication' },
      { icon: '🌐', label: 'NSG', detail: 'Overly permissive inbound rules (SSH/RDP/3389 public)' },
      { icon: '📊', label: 'Monitor', detail: 'Security alerts, diagnostic logs enabled' },
    ],
    frameworks: ['CIS Azure 2.0', 'Azure Security Benchmark v3', 'ISO 27001:2013'],
  },
  {
    id: 'gcp',
    name: 'Google Cloud Platform',
    icon: '🟡',
    badge: 'Prowler',
    color: 'from-yellow-900/40 to-yellow-800/20 border-yellow-500/40',
    activeColor: 'from-yellow-500/30 to-yellow-400/10 border-yellow-400',
    description: 'IAM, GCS, Compute, GKE, Cloud SQL...',
    tool: 'Prowler (multi-cloud security audit)',
    duration: '5 – 15 min',
    checks: [
      { icon: '👤', label: 'IAM', detail: 'Service accounts, key rotation, bindings trop permissifs' },
      { icon: '🪣', label: 'GCS', detail: 'Buckets publics, ACLs allUsers/allAuthUsers, logging' },
      { icon: '🖥️', label: 'Compute', detail: 'Firewall rules, OS Login, exposed metadata' },
      { icon: '⎈', label: 'GKE', detail: 'RBAC, Workload Identity, private network, nodes' },
      { icon: '🗃️', label: 'Cloud SQL', detail: 'Mandatory SSL, authorized IPs, encrypted backups' },
      { icon: '⚡', label: 'Functions', detail: 'Authentification requise, permissions IAM minimales' },
      { icon: '🌐', label: 'VPC', detail: 'Flow logs, firewall 0.0.0.0/0 rules, VPC Sharing' },
    ],
    frameworks: ['CIS GCP 2.0', 'GCP Audit Manager'],
  },
  {
    id: 'ovh',
    name: 'OVHcloud',
    icon: '🔵',
    badge: 'API Native',
    badgeColor: 'bg-blue-500/20 text-blue-300 border-blue-500/40',
    color: 'from-blue-900/40 to-blue-800/20 border-blue-500/40',
    activeColor: 'from-blue-500/30 to-blue-400/10 border-blue-400',
    description: 'VPS, Bare Metal, Public Cloud, Firewall OVH...',
    tool: 'Audit API OVH native + Nmap + Nuclei',
    duration: '3 – 10 min',
    note: 'Audit en lecture seule via l\'API OVH. Compatible OVHcloud, Kimsufi, So You Start.',
    checks: [
      { icon: '🖥️', label: 'VPS / Bare Metal', detail: 'Exposed IPs, monitoring, snapshots, reverse DNS' },
      { icon: '☁️', label: 'Public Cloud', detail: 'Instances, Object Storage (public access), private networks' },
      { icon: '🛡️', label: 'Firewall OVH', detail: 'Filtering rules, exposed ports, DDoS mitigation' },
      { icon: '🌐', label: 'Load Balancers', detail: 'Configuration SSL/TLS, certificats, backends' },
      { icon: '📡', label: 'DNS / Domaines', detail: 'Exposed DNS zones, sensitive records' },
      { icon: '🔒', label: 'SSL / TLS', detail: 'Expired certificates, weak cipher suites (via Nmap)' },
      { icon: '🔍', label: 'Exposed ports', detail: 'Nmap scan on all OVH IPs in your account' },
    ],
    frameworks: ['ISO 27001:2022', 'NIS2 European', 'HDS (Health Data Host)'],
  },
  {
    id: 'scaleway',
    name: 'Scaleway',
    icon: '🟣',
    badge: 'API Native',
    badgeColor: 'bg-purple-500/20 text-purple-300 border-purple-500/40',
    color: 'from-purple-900/40 to-purple-800/20 border-purple-500/40',
    activeColor: 'from-purple-500/30 to-purple-400/10 border-purple-400',
    description: 'Instances, Object Storage, Security Groups...',
    tool: 'Audit API Scaleway + Nmap + Nuclei',
    duration: '3 – 10 min',
    note: 'Audit via Scaleway IAM Token (read-only). GDPR compliant, hosted in France.',
    checks: [
      { icon: '🖥️', label: 'Instances', detail: 'Public IPs, open ports, unencrypted volumes' },
      { icon: '🪣', label: 'Object Storage', detail: 'ACLs publiques, versioning, CORS permissif' },
      { icon: '🛡️', label: 'Security groups', detail: 'Overly permissive inbound rules (0.0.0.0/0)' },
      { icon: '⚖️', label: 'Load Balancers', detail: 'SSL configuration, certificates, exposed backends' },
      { icon: '📦', label: 'Container Registry', detail: 'Unintended public images, vulnerabilities' },
      { icon: '🌐', label: 'VPC & Networks', detail: 'Private subnets, routes, Private Networks' },
      { icon: '🔍', label: 'Exposed ports', detail: 'Nmap scan on all Scaleway IPs in your account' },
    ],
    frameworks: ['ISO 27001:2022', 'SecNumCloud (ANSSI)', 'NIS2 European', 'GDPR'],
  },
  {
    id: 'o2switch',
    name: 'O2Switch',
    icon: '🟠',
    badge: 'Hosting Web',
    badgeColor: 'bg-orange-500/20 text-orange-300 border-orange-500/40',
    color: 'from-orange-900/40 to-orange-800/20 border-orange-500/40',
    activeColor: 'from-orange-500/30 to-orange-400/10 border-orange-400',
    description: 'cPanel, SSL, Headers HTTP, CMS, PHP...',
    tool: 'Nmap + Nikto + Nuclei + SSL Labs + cPanel API',
    duration: '5 – 20 min',
    note: 'Audit of infrastructure hosted on O2Switch (shared cPanel hosting).',
    checks: [
      { icon: '🔒', label: 'SSL / TLS', detail: 'Expiration certificats, TLS 1.0/1.1, cipher suites faibles' },
      { icon: '📋', label: 'Headers HTTP', detail: 'HSTS, CSP, X-Frame-Options, X-Content-Type-Options' },
      { icon: '🌐', label: 'Domaines & DNS', detail: 'Exposed subdomains, missing SPF/DKIM/DMARC' },
      { icon: '🔍', label: 'Exposed ports', detail: 'Nmap scan of hosting IP, exposed services' },
      { icon: '📱', label: 'CMS / Applications', detail: 'WordPress, Joomla, Prestashop: vulnerable versions and plugins' },
      { icon: '⚙️', label: 'PHP / Serveur', detail: 'Exposed PHP version, visible debug errors' },
      { icon: '🕷️', label: 'Web vulnerabilities', detail: 'Nuclei scan: LFI, RFI, XSS, SQLi, insecure upload' },
    ],
    frameworks: ['OWASP Top 10', 'ISO 27001 (partiel)', 'Security Headers Best Practices'],
  },
  {
    id: 'unitel',
    name: 'Unitel Cloud',
    icon: '🇫🇷',
    badge: 'Cloud Souverain',
    badgeColor: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40',
    color: 'from-indigo-900/40 to-indigo-800/20 border-indigo-500/40',
    activeColor: 'from-indigo-500/30 to-indigo-400/10 border-indigo-400',
    description: 'Core Cloud, Edge Cloud, S3, 10G/100G Networks...',
    tool: 'OpenStack API + Nmap + Nuclei',
    duration: '5 – 15 min',
    note: 'French sovereign cloud certified HDS. OpenStack-based infrastructure. Data stays in France (EEA). Credentials available at extranet.unitel.fr.',
    checks: [
      { icon: '🖥️', label: 'Core Cloud', detail: 'CPU/GPU VMs, public images, security groups, SSH keys' },
      { icon: '⚡', label: 'Edge Cloud', detail: 'Edge instances, security latency, exposed network access' },
      { icon: '🪣', label: 'Stockage S3', detail: 'Buckets publics, ACLs, chiffrement, versioning' },
      { icon: '🌐', label: 'Networks & VPC', detail: 'Unused floating IPs, overly permissive security groups' },
      { icon: '🔒', label: 'Exposed SSL / TLS', detail: 'Exposed services: expired certificates, weak TLS (Nmap)' },
      { icon: '🛡️', label: 'Firewall / ACL', detail: 'Inbound 0.0.0.0/0 rules, critical ports (22, 3389, 5432) exposed' },
      { icon: '🔍', label: 'Surface externe', detail: 'Nuclei scan on allocated public IPs: CVE, web misconfigs' },
    ],
    frameworks: ['ISO 27001:2022', 'HDS (Health Data Host)', 'NIS2 European', 'Sovereign GDPR'],
  },
  {
    id: 'exoscale',
    name: 'Exoscale',
    icon: '🟧',
    badge: 'API Native + PoC',
    badgeColor: 'bg-red-500/20 text-red-300 border-red-500/40',
    color: 'from-rose-900/40 to-rose-800/20 border-rose-500/40',
    activeColor: 'from-rose-500/30 to-rose-400/10 border-rose-400',
    description: 'SOS Object Storage (S3), Compute, Security Groups, IAM...',
    tool: 'Audit API Exoscale (SOS S3 + Compute v2) + PoC accès anonyme',
    duration: '3 – 10 min',
    note: 'Swiss/EU IaaS. Audit via une clé IAM API Exoscale (id + secret, lecture seule recommandée). Les buckets SOS publics sont prouvés par un accès anonyme réel.',
    checks: [
      { icon: '🪣', label: 'SOS (S3)', detail: 'Buckets publics (PoC accès anonyme), chiffrement, ACL/policy' },
      { icon: '🛡️', label: 'Security Groups', detail: 'Règles ingress 0.0.0.0/0 sur ports sensibles (SSH/RDP/DB)' },
      { icon: '🔑', label: 'IAM API Keys', detail: 'Clés non restreintes (accès complet au compte)' },
      { icon: '🖥️', label: 'Compute', detail: 'Instances exposées, zones ch-gva-2 / de-fra-1 / at-vie-1...' },
    ],
    frameworks: ['CIS Controls v8', 'ISO 27001:2022', 'GDPR (Switzerland/EU)'],
  },
  {
    id: 'infomaniak',
    name: 'Infomaniak Public Cloud',
    icon: '🇨🇭',
    badge: 'OpenStack + PoC',
    badgeColor: 'bg-sky-500/20 text-sky-300 border-sky-500/40',
    color: 'from-sky-900/40 to-sky-800/20 border-sky-500/40',
    activeColor: 'from-sky-500/30 to-sky-400/10 border-sky-400',
    description: 'OpenStack, S3/Swift Object Storage, Security Groups, Instances...',
    tool: 'OpenStack SDK + S3 (boto3) + PoC accès anonyme',
    duration: '4 – 12 min',
    note: 'Cloud public suisse basé OpenStack. Audit via Application Credential OpenStack (id + secret) et/ou clés EC2/S3. Buckets publics prouvés par accès anonyme réel.',
    checks: [
      { icon: '🛡️', label: 'Security Groups', detail: 'Règles ingress 0.0.0.0/0 (tous ports / ports sensibles)' },
      { icon: '🪣', label: 'S3 / Swift', detail: 'Buckets S3 & conteneurs Swift publics (PoC accès anonyme)' },
      { icon: '🖥️', label: 'Instances', detail: 'IP publiques directement joignables' },
      { icon: '🔑', label: 'Identity (Keystone)', detail: 'Application credentials, rôles, keypairs' },
    ],
    frameworks: ['CIS Controls v8', 'ISO 27001:2022', 'Swiss nFADP / GDPR'],
  },
];

const AWS_SERVICES = [
  'iam', 's3', 'ec2', 'rds', 'lambda', 'cloudtrail',
  'cloudwatch', 'config', 'guardduty', 'securityhub', 'kms', 'vpc',
];

const PasswordField = ({ label, value, onChange, placeholder }) => {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block text-sm text-gray-400 mb-1">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 pr-10 text-white text-sm focus:outline-none focus:border-brand-green/50"
        />
        <button
          type="button"
          onClick={() => setShow(s => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

const CloudScanWizard = ({ isOpen, onClose, onComplete }) => {
  const { activeWorkspace } = useWorkspace();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [provider, setProvider] = useState('aws');
  const [compliance, setCompliance] = useState('cis_level2_aws_2.0');
  const [selectedServices, setSelectedServices] = useState([]);

  // AWS credentials
  const [awsKey, setAwsKey] = useState('');
  const [awsSecret, setAwsSecret] = useState('');
  const [awsToken, setAwsToken] = useState('');
  const [awsRegion, setAwsRegion] = useState('eu-west-1');

  // Azure credentials
  const [azureTenant, setAzureTenant] = useState('');
  const [azureClient, setAzureClient] = useState('');
  const [azureSecret, setAzureSecret] = useState('');
  const [azureSubscription, setAzureSubscription] = useState('');

  // GCP credentials
  const [gcpProject, setGcpProject] = useState('');
  const [gcpJson, setGcpJson] = useState('');

  // OVH credentials
  const [ovhAppKey, setOvhAppKey] = useState('');
  const [ovhAppSecret, setOvhAppSecret] = useState('');
  const [ovhConsumerKey, setOvhConsumerKey] = useState('');
  const [ovhEndpoint, setOvhEndpoint] = useState('ovh-eu');

  // Scaleway credentials
  const [scwAccessKey, setScwAccessKey] = useState('');
  const [scwSecretKey, setScwSecretKey] = useState('');
  const [scwOrgId, setScwOrgId] = useState('');
  const [scwRegion, setScwRegion] = useState('fr-par');

  // O2Switch credentials
  const [o2Domain, setO2Domain] = useState('');
  const [o2CpanelUrl, setO2CpanelUrl] = useState('');
  const [o2CpanelUser, setO2CpanelUser] = useState('');
  const [o2CpanelToken, setO2CpanelToken] = useState('');

  // Unitel credentials
  const [unitelAuthUrl, setUnitelAuthUrl] = useState('https://api.unitel.fr:5000/v3');
  const [unitelUser, setUnitelUser] = useState('');
  const [unitelPassword, setUnitelPassword] = useState('');
  const [unitelProject, setUnitelProject] = useState('');
  const [unitelDomain, setUnitelDomain] = useState('Default');
  const [unitelRegion, setUnitelRegion] = useState('fr-paris');

  // Exoscale credentials
  const [exoKey, setExoKey] = useState('');
  const [exoSecret, setExoSecret] = useState('');
  const [exoZones, setExoZones] = useState('');

  // Infomaniak (OpenStack) credentials
  const [ikEndpoint, setIkEndpoint] = useState('pub1');
  const [ikAppCredId, setIkAppCredId] = useState('');
  const [ikAppCredSecret, setIkAppCredSecret] = useState('');
  const [ikRegion, setIkRegion] = useState('dc3-a');
  const [ikS3Key, setIkS3Key] = useState('');
  const [ikS3Secret, setIkS3Secret] = useState('');

  const [scanName, setScanName] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setError('');
      setLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const complianceOptions = cloudScan.getCompliances(provider);
    if (complianceOptions.length > 0) {
      setCompliance(complianceOptions[0].value);
    }
    setSelectedServices([]);
    setScanName(`Cloud Audit ${provider.toUpperCase()} — ${new Date().toLocaleDateString('fr-FR')}`);
  }, [provider]);

  const getTarget = () => {
    if (provider === 'aws') return awsKey ? `aws-${awsKey.slice(0, 8)}...` : 'aws-cloud';
    if (provider === 'azure') return azureSubscription || 'azure-cloud';
    if (provider === 'gcp') return gcpProject || 'gcp-cloud';
    if (provider === 'ovh') return ovhAppKey ? `ovh-${ovhEndpoint}` : 'ovh-cloud';
    if (provider === 'scaleway') return scwAccessKey ? `scaleway-${scwRegion}` : 'scaleway-cloud';
    if (provider === 'o2switch') return o2Domain || 'o2switch-hosting';
    if (provider === 'unitel') return unitelProject || unitelUser ? `unitel-${unitelRegion}` : 'unitel-cloud';
    if (provider === 'exoscale') return exoKey ? `exoscale-${exoKey.slice(0, 8)}` : 'exoscale-cloud';
    if (provider === 'infomaniak') return `infomaniak-${ikEndpoint}`;
    return 'cloud';
  };

  const validateStep = () => {
    setError('');
    if (step === 2) {
      if (provider === 'aws' && (!awsKey || !awsSecret)) {
        setError('Access Key ID et Secret Access Key sont requis.');
        return false;
      }
      if (provider === 'azure' && (!azureTenant || !azureClient || !azureSecret)) {
        setError('Tenant ID, Client ID et Client Secret sont requis.');
        return false;
      }
      if (provider === 'gcp' && !gcpProject) {
        setError('Project ID est requis.');
        return false;
      }
      if (provider === 'ovh' && (!ovhAppKey || !ovhAppSecret || !ovhConsumerKey)) {
        setError('Application Key, Application Secret et Consumer Key sont requis.');
        return false;
      }
      if (provider === 'scaleway' && (!scwAccessKey || !scwSecretKey)) {
        setError('Access Key et Secret Key Scaleway sont requis.');
        return false;
      }
      if (provider === 'o2switch' && !o2Domain) {
        setError('Le domaine principal est requis.');
        return false;
      }
      if (provider === 'unitel' && (!unitelUser || !unitelPassword)) {
        setError('Nom d\'utilisateur et mot de passe Unitel sont requis.');
        return false;
      }
      if (provider === 'exoscale' && (!exoKey || !exoSecret)) {
        setError('Exoscale API Key et API Secret sont requis.');
        return false;
      }
      if (provider === 'infomaniak' && !((ikAppCredId && ikAppCredSecret) || (ikS3Key && ikS3Secret))) {
        setError('Fournissez une Application Credential OpenStack (id + secret) et/ou des clés S3 (access + secret).');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setStep(s => s + 1);
  };

  const handleLaunch = async () => {
    setLoading(true);
    setError('');
    try {
      const scanData = {
        name: scanName,
        target: getTarget(),
        scan_type: `cloud_${provider}`,
        workspace_id: activeWorkspace?.id || null,
        config: {
          scan_type: `cloud_${provider}`,
          cloud_provider: provider,
          cloud_compliance: compliance,
          cloud_services: selectedServices.length > 0 ? selectedServices : null,
          // AWS
          aws_access_key_id: awsKey || undefined,
          aws_secret_access_key: awsSecret || undefined,
          aws_session_token: awsToken || undefined,
          aws_region: awsRegion,
          // Azure
          azure_tenant_id: azureTenant || undefined,
          azure_client_id: azureClient || undefined,
          azure_client_secret: azureSecret || undefined,
          azure_subscription_id: azureSubscription || undefined,
          // GCP
          gcp_project_id: gcpProject || undefined,
          gcp_service_account_json: gcpJson || undefined,
          // OVH
          ovh_app_key: ovhAppKey || undefined,
          ovh_app_secret: ovhAppSecret || undefined,
          ovh_consumer_key: ovhConsumerKey || undefined,
          ovh_endpoint: ovhEndpoint || undefined,
          // Scaleway
          scw_access_key: scwAccessKey || undefined,
          scw_secret_key: scwSecretKey || undefined,
          scw_organization_id: scwOrgId || undefined,
          scw_region: scwRegion || undefined,
          // O2Switch
          o2_domain: o2Domain || undefined,
          o2_cpanel_url: o2CpanelUrl || undefined,
          o2_cpanel_user: o2CpanelUser || undefined,
          o2_cpanel_token: o2CpanelToken || undefined,
          // Unitel (OpenStack)
          unitel_auth_url: unitelAuthUrl || undefined,
          unitel_username: unitelUser || undefined,
          unitel_password: unitelPassword || undefined,
          unitel_project_name: unitelProject || undefined,
          unitel_domain_name: unitelDomain || undefined,
          unitel_region: unitelRegion || undefined,
          // Exoscale
          exoscale_api_key: exoKey || undefined,
          exoscale_api_secret: exoSecret || undefined,
          exoscale_zones: exoZones ? exoZones.split(',').map(z => z.trim()).filter(Boolean) : undefined,
          // Infomaniak (OpenStack Public Cloud)
          infomaniak_endpoint: ikEndpoint || undefined,
          infomaniak_app_credential_id: ikAppCredId || undefined,
          infomaniak_app_credential_secret: ikAppCredSecret || undefined,
          infomaniak_region: ikRegion || undefined,
          infomaniak_s3_access_key: ikS3Key || undefined,
          infomaniak_s3_secret_key: ikS3Secret || undefined,
          // General
          ai_analysis: true,
        }
      };

      const result = await scansApi.create(scanData);
      onComplete && onComplete(result);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Erreur lors du lancement du scan');
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (svc) => {
    setSelectedServices(prev =>
      prev.includes(svc) ? prev.filter(s => s !== svc) : [...prev, svc]
    );
  };

  if (!isOpen) return null;

  const complianceOptions = cloudScan.getCompliances(provider);
  const selectedProvider = PROVIDERS.find(p => p.id === provider);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-brand-black-light border border-brand-green/30 rounded-2xl w-full max-w-7xl shadow-2xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-green/20 rounded-xl flex items-center justify-center">
              <Cloud className="w-5 h-5 text-brand-green" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">Scan Cloud</h2>
              <p className="text-gray-400 text-sm">Cloud security audit — AWS, Azure, GCP, OVH, Scaleway, O2Switch</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 px-6 pt-4 flex-shrink-0">
          {['Provider', 'Credentials', 'Scope', 'Lancement'].map((label, i) => (
            <React.Fragment key={i}>
              <div className={`flex items-center gap-1.5 ${step > i + 1 ? 'text-brand-green' : step === i + 1 ? 'text-white' : 'text-gray-600'}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold border ${
                  step > i + 1 ? 'bg-brand-green/20 border-brand-green text-brand-green' :
                  step === i + 1 ? 'bg-brand-green border-brand-green text-brand-black' :
                  'border-gray-700 text-gray-600'
                }`}>
                  {step > i + 1 ? '✓' : i + 1}
                </div>
                <span className="text-xs hidden sm:block">{label}</span>
              </div>
              {i < 3 && <div className={`flex-1 h-px ${step > i + 1 ? 'bg-brand-green/40' : 'bg-gray-800'}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Content — scrollable */}
        <div className="p-6 overflow-y-auto flex-1">

          {/* Step 1: Provider */}
          {step === 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: provider list */}
              <div className="space-y-3">
                <h3 className="text-white font-semibold mb-3">Select your cloud provider</h3>
                {PROVIDERS.map(p => (
                  <button
                    key={p.id}
                    onClick={() => setProvider(p.id)}
                    className={`flex items-center gap-4 p-3.5 rounded-xl border bg-gradient-to-r transition-all text-left w-full ${
                      provider === p.id ? p.activeColor : p.color
                    }`}
                  >
                    <span className="text-2xl">{p.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-medium text-sm">{p.name}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded border font-medium ${
                          p.badgeColor || 'bg-brand-green/20 text-brand-green border-brand-green/40'
                        }`}>{p.badge}</span>
                      </div>
                      <div className="text-gray-400 text-xs mt-0.5 truncate">{p.description}</div>
                    </div>
                    {provider === p.id && <CheckCircle className="w-5 h-5 text-brand-green flex-shrink-0" />}
                  </button>
                ))}
              </div>

              {/* Right: detail panel */}
              {selectedProvider && (
                <div className="space-y-4">
                  <div className="bg-brand-black rounded-xl border border-gray-700 p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="text-3xl">{selectedProvider.icon}</span>
                      <div>
                        <h4 className="text-white font-bold">{selectedProvider.name}</h4>
                        <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                          <span>🔧 {selectedProvider.tool}</span>
                        </div>
                      </div>
                    </div>

                    {selectedProvider.note && (
                      <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-2.5 flex items-start gap-2 mb-3">
                        <Info className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <p className="text-blue-300 text-xs">{selectedProvider.note}</p>
                      </div>
                    )}

                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider">What is audited</span>
                      <span className="text-xs text-gray-500">⏱ {selectedProvider.duration}</span>
                    </div>
                    <div className="space-y-2">
                      {selectedProvider.checks.map((check, i) => (
                        <div key={i} className="flex items-start gap-2.5 p-2 rounded-lg bg-brand-black-light border border-gray-800">
                          <span className="text-base flex-shrink-0">{check.icon}</span>
                          <div>
                            <span className="text-white text-xs font-semibold">{check.label}</span>
                            <span className="text-gray-400 text-xs"> — {check.detail}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-3 pt-3 border-t border-gray-800">
                      <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider block mb-2">Available compliance frameworks</span>
                      <div className="flex flex-wrap gap-1.5">
                        {selectedProvider.frameworks.map((fw, i) => (
                          <span key={i} className="text-xs bg-brand-green/10 border border-brand-green/30 text-brand-green px-2 py-0.5 rounded-full">
                            {fw}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Credentials */}
          {step === 2 && (
            <div className="space-y-4 max-w-2xl mx-auto">
              <div className="flex items-center gap-2 mb-2">
                <Key className="w-4 h-4 text-brand-green" />
                <h3 className="text-white font-semibold">Credentials {selectedProvider?.name}</h3>
              </div>
              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-yellow-300 text-xs">
                  Utilisez un compte avec des permissions <strong>en lecture seule</strong> pour l'audit.
                  Credentials are not stored in the database.
                </p>
              </div>

              {provider === 'aws' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Access Key ID *</label>
                    <input type="text" value={awsKey} onChange={e => setAwsKey(e.target.value)}
                      placeholder="AKIAIOSFODNN7EXAMPLE"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
                  </div>
                  <PasswordField label="Secret Access Key *" value={awsSecret}
                    onChange={e => setAwsSecret(e.target.value)} placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY" />
                  <PasswordField label="Session Token (optionnel)" value={awsToken}
                    onChange={e => setAwsToken(e.target.value)} placeholder="For assumed roles (AssumeRole)" />
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">AWS Region</label>
                    <select value={awsRegion} onChange={e => setAwsRegion(e.target.value)}
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50">
                      {['eu-west-1','eu-west-2','eu-west-3','eu-central-1','us-east-1','us-east-2','us-west-1','us-west-2','ap-southeast-1','ap-southeast-2','ap-northeast-1','sa-east-1'].map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {provider === 'azure' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Tenant ID *</label>
                    <input type="text" value={azureTenant} onChange={e => setAzureTenant(e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Client ID (App ID) *</label>
                    <input type="text" value={azureClient} onChange={e => setAzureClient(e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
                  </div>
                  <PasswordField label="Client Secret *" value={azureSecret}
                    onChange={e => setAzureSecret(e.target.value)} placeholder="Client secret de votre App Registration" />
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Subscription ID (optionnel)</label>
                    <input type="text" value={azureSubscription} onChange={e => setAzureSubscription(e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
                  </div>
                </div>
              )}

              {provider === 'gcp' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Project ID *</label>
                    <input type="text" value={gcpProject} onChange={e => setGcpProject(e.target.value)}
                      placeholder="my-gcp-project-123456"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Service Account JSON (optionnel)</label>
                    <textarea value={gcpJson} onChange={e => setGcpJson(e.target.value)}
                      placeholder='{"type": "service_account", "project_id": "...", ...}'
                      rows={5}
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-brand-green/50 resize-none" />
                    <p className="text-gray-500 text-xs mt-1">Laissez vide pour utiliser les Application Default Credentials</p>
                  </div>
                </div>
              )}

              {provider === 'ovh' && (
                <div className="space-y-3">
                  <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                    <p className="text-blue-300 text-xs">
                      Create your credentials at <strong>api.ovh.com/createToken</strong> with <code className="bg-black/40 px-1 rounded">GET</code> rights on <code className="bg-black/40 px-1 rounded">/*</code>
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Endpoint OVH</label>
                    <select value={ovhEndpoint} onChange={e => setOvhEndpoint(e.target.value)}
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50">
                      <option value="ovh-eu">ovh-eu (Europe)</option>
                      <option value="ovh-us">ovh-us (United States)</option>
                      <option value="ovh-ca">ovh-ca (Canada)</option>
                      <option value="kimsufi-eu">kimsufi-eu</option>
                      <option value="kimsufi-ca">kimsufi-ca</option>
                      <option value="soyoustart-eu">soyoustart-eu</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Application Key *</label>
                    <input type="text" value={ovhAppKey} onChange={e => setOvhAppKey(e.target.value)}
                      placeholder="xxxxxxxxxxxxxxxx"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
                  </div>
                  <PasswordField label="Application Secret *" value={ovhAppSecret}
                    onChange={e => setOvhAppSecret(e.target.value)} placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
                  <PasswordField label="Consumer Key *" value={ovhConsumerKey}
                    onChange={e => setOvhConsumerKey(e.target.value)} placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
                </div>
              )}

              {provider === 'scaleway' && (
                <div className="space-y-3">
                  <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3">
                    <p className="text-purple-300 text-xs">
                      Create an IAM API Key on <strong>console.scaleway.com</strong> with a <strong>read-only</strong> policy.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Region</label>
                    <select value={scwRegion} onChange={e => setScwRegion(e.target.value)}
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50">
                      <option value="fr-par">fr-par (Paris)</option>
                      <option value="nl-ams">nl-ams (Amsterdam)</option>
                      <option value="pl-waw">pl-waw (Varsovie)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Access Key *</label>
                    <input type="text" value={scwAccessKey} onChange={e => setScwAccessKey(e.target.value)}
                      placeholder="SCWXXXXXXXXXXXXXXXXX"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
                  </div>
                  <PasswordField label="Secret Key *" value={scwSecretKey}
                    onChange={e => setScwSecretKey(e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Organization ID (optionnel)</label>
                    <input type="text" value={scwOrgId} onChange={e => setScwOrgId(e.target.value)}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
                  </div>
                </div>
              )}

              {provider === 'unitel' && (
                <div className="space-y-3">
                  <div className="bg-indigo-900/20 border border-indigo-500/30 rounded-lg p-3">
                    <p className="text-indigo-300 text-xs">
                      Log in with your Unitel credentials (extranet.unitel.fr). Unitel infrastructure is based on OpenStack; the scan uses the Keystone API to list your cloud resources.
                      Si l'API OpenStack n'est pas accessible, le scan analyse automatiquement la surface externe (IPs publiques, ports, SSL).
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">URL Auth OpenStack (Keystone)</label>
                    <input type="text" value={unitelAuthUrl} onChange={e => setUnitelAuthUrl(e.target.value)}
                      placeholder="https://api.unitel.fr:5000/v3"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-brand-green/50" />
                    <p className="text-gray-500 text-xs mt-1">Disponible dans votre espace client Unitel (extranet.unitel.fr)</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Region</label>
                      <select value={unitelRegion} onChange={e => setUnitelRegion(e.target.value)}
                        className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50">
                        <option value="fr-paris">fr-paris (Paris)</option>
                        <option value="fr-lyon">fr-lyon (Lyon)</option>
                        <option value="fr-marseille">fr-marseille (Marseille)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Domaine OpenStack</label>
                      <input type="text" value={unitelDomain} onChange={e => setUnitelDomain(e.target.value)}
                        placeholder="Default"
                        className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Nom d'utilisateur *</label>
                    <input type="text" value={unitelUser} onChange={e => setUnitelUser(e.target.value)}
                      placeholder="utilisateur@monentreprise.fr"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
                  </div>
                  <PasswordField label="Mot de passe *" value={unitelPassword}
                    onChange={e => setUnitelPassword(e.target.value)} placeholder="Mot de passe extranet Unitel" />
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Nom du projet (optionnel)</label>
                    <input type="text" value={unitelProject} onChange={e => setUnitelProject(e.target.value)}
                      placeholder="mon-projet-cloud"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
                    <p className="text-gray-500 text-xs mt-1">Laissez vide pour auditer tous les projets de votre compte</p>
                  </div>
                </div>
              )}

              {provider === 'exoscale' && (
                <div className="space-y-3">
                  <div className="bg-rose-900/20 border border-rose-500/30 rounded-lg p-3">
                    <p className="text-rose-300 text-xs">
                      Create a read-only IAM API key in the Exoscale console (IAM → Keys). The scan audits SOS object storage (S3), security groups and IAM, and proves public buckets via a real anonymous access test. The secret is shown only once at creation.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">API Key (id) *</label>
                    <input type="text" value={exoKey} onChange={e => setExoKey(e.target.value)}
                      placeholder="EXOxxxxxxxxxxxxxxxxxxxx"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-brand-green/50" />
                  </div>
                  <PasswordField label="API Secret *" value={exoSecret}
                    onChange={e => setExoSecret(e.target.value)} placeholder="Secret affiché une seule fois à la création de la clé" />
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Zones (optionnel)</label>
                    <input type="text" value={exoZones} onChange={e => setExoZones(e.target.value)}
                      placeholder="ch-gva-2, de-fra-1, at-vie-1 (vide = toutes les zones)"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-brand-green/50" />
                    <p className="text-gray-500 text-xs mt-1">Zones séparées par des virgules. Vide = ch-gva-2, ch-dk-2, de-fra-1, de-muc-1, at-vie-1, at-vie-2, bg-sof-1</p>
                  </div>
                </div>
              )}

              {provider === 'infomaniak' && (
                <div className="space-y-3">
                  <div className="bg-sky-900/20 border border-sky-500/30 rounded-lg p-3">
                    <p className="text-sky-300 text-xs">
                      Infomaniak Public Cloud is OpenStack-based. Create an Application Credential (`openstack application credential create --role reader`) for a read-only audit of security groups, instances and Swift containers. Optionally add EC2/S3 keys (`openstack ec2 credentials create`) to audit S3 buckets with an anonymous public-access PoC.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Endpoint</label>
                      <select value={ikEndpoint} onChange={e => setIkEndpoint(e.target.value)}
                        className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50">
                        <option value="pub1">pub1 (api.pub1.infomaniak.cloud)</option>
                        <option value="pub2">pub2 (api.pub2.infomaniak.cloud)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Region</label>
                      <input type="text" value={ikRegion} onChange={e => setIkRegion(e.target.value)}
                        placeholder="dc3-a"
                        className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-brand-green/50" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Application Credential ID</label>
                    <input type="text" value={ikAppCredId} onChange={e => setIkAppCredId(e.target.value)}
                      placeholder="OpenStack application credential id"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-brand-green/50" />
                  </div>
                  <PasswordField label="Application Credential Secret" value={ikAppCredSecret}
                    onChange={e => setIkAppCredSecret(e.target.value)} placeholder="Secret de l'application credential OpenStack" />
                  <div className="pt-2 border-t border-gray-800">
                    <p className="text-gray-500 text-xs mb-3">Object Storage S3 — optionnel (pour auditer les buckets S3)</p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">S3 Access Key (EC2)</label>
                        <input type="text" value={ikS3Key} onChange={e => setIkS3Key(e.target.value)}
                          placeholder="openstack ec2 credentials create → access"
                          className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-brand-green/50" />
                      </div>
                      <PasswordField label="S3 Secret Key (EC2)" value={ikS3Secret}
                        onChange={e => setIkS3Secret(e.target.value)} placeholder="openstack ec2 credentials create → secret" />
                    </div>
                  </div>
                </div>
              )}

              {provider === 'o2switch' && (
                <div className="space-y-3">
                  <div className="bg-orange-900/20 border border-orange-500/30 rounded-lg p-3">
                    <p className="text-orange-300 text-xs">
                      For O2Switch, the scan audits your hosted infrastructure. The domain is sufficient for a web surface audit. cPanel credentials are optional for a more complete audit.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">Domaine principal *</label>
                    <input type="text" value={o2Domain} onChange={e => setO2Domain(e.target.value)}
                      placeholder="monsite.fr"
                      className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
                  </div>
                  <div className="pt-2 border-t border-gray-800">
                    <p className="text-gray-500 text-xs mb-3">cPanel — optional (for hosting configuration audit)</p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">URL cPanel</label>
                        <input type="text" value={o2CpanelUrl} onChange={e => setO2CpanelUrl(e.target.value)}
                          placeholder="https://panel.o2switch.net:2083"
                          className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Nom d'utilisateur cPanel</label>
                        <input type="text" value={o2CpanelUser} onChange={e => setO2CpanelUser(e.target.value)}
                          placeholder="cpanel_username"
                          className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
                      </div>
                      <PasswordField label="API Token cPanel" value={o2CpanelToken}
                        onChange={e => setO2CpanelToken(e.target.value)} placeholder="Token generated in cPanel > Manage API Tokens" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Scope */}
          {step === 3 && (
            <div className="space-y-4 max-w-2xl mx-auto">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-brand-green" />
                <h3 className="text-white font-semibold">Audit scope</h3>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Compliance framework</label>
                <div className="space-y-2">
                  {complianceOptions.map(opt => (
                    <button key={opt.value} onClick={() => setCompliance(opt.value)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                        compliance === opt.value
                          ? 'border-brand-green/60 bg-brand-green/10 text-white'
                          : 'border-gray-700 bg-brand-black text-gray-400 hover:border-gray-600'
                      }`}>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        compliance === opt.value ? 'border-brand-green' : 'border-gray-600'
                      }`}>
                        {compliance === opt.value && <div className="w-2 h-2 rounded-full bg-brand-green" />}
                      </div>
                      <span className="text-sm font-medium">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {provider === 'aws' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Services AWS (optionnel — laissez vide pour tous)
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {AWS_SERVICES.map(svc => (
                      <button key={svc} onClick={() => toggleService(svc)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                          selectedServices.includes(svc)
                            ? 'border-brand-green/60 bg-brand-green/15 text-brand-green'
                            : 'border-gray-700 bg-brand-black text-gray-400 hover:border-gray-600'
                        }`}>
                        {svc.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Launch */}
          {step === 4 && (
            <div className="space-y-4 max-w-2xl mx-auto">
              <h3 className="text-white font-semibold">Summary & Launch</h3>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Nom du scan</label>
                <input type="text" value={scanName} onChange={e => setScanName(e.target.value)}
                  className="w-full bg-brand-black border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand-green/50" />
              </div>

              <div className="bg-brand-black rounded-xl border border-gray-800 p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{selectedProvider?.icon}</span>
                  <div>
                    <div className="text-white font-medium">{selectedProvider?.name}</div>
                    <div className="text-gray-400 text-sm">{getTarget()}</div>
                  </div>
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded border ${
                    selectedProvider?.badgeColor || 'bg-brand-green/20 text-brand-green border-brand-green/40'
                  }`}>{selectedProvider?.badge}</span>
                </div>
                <div className="h-px bg-gray-800" />
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Framework</span>
                    <div className="text-white mt-0.5">
                      {complianceOptions.find(c => c.value === compliance)?.label || compliance}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Outil d'audit</span>
                    <div className="text-white mt-0.5 text-xs">{selectedProvider?.tool?.split(' (')[0]}</div>
                  </div>
                  {provider === 'aws' && selectedServices.length > 0 && (
                    <div>
                      <span className="text-gray-500">Services</span>
                      <div className="text-white mt-0.5">{selectedServices.join(', ')}</div>
                    </div>
                  )}
                  {provider === 'aws' && (
                    <div>
                      <span className="text-gray-500">Region</span>
                      <div className="text-white mt-0.5">{awsRegion}</div>
                    </div>
                  )}
                  {provider === 'scaleway' && (
                    <div>
                      <span className="text-gray-500">Region</span>
                      <div className="text-white mt-0.5">{scwRegion}</div>
                    </div>
                  )}
                  {provider === 'ovh' && (
                    <div>
                      <span className="text-gray-500">Endpoint</span>
                      <div className="text-white mt-0.5">{ovhEndpoint}</div>
                    </div>
                  )}
                  {provider === 'unitel' && (
                    <div>
                      <span className="text-gray-500">Region</span>
                      <div className="text-white mt-0.5">{unitelRegion}</div>
                    </div>
                  )}
                  {activeWorkspace && (
                    <div>
                      <span className="text-gray-500">Workspace</span>
                      <div className="text-white mt-0.5">{activeWorkspace.name}</div>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Estimated duration</span>
                    <div className="text-white mt-0.5">{selectedProvider?.duration}</div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3 flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                <p className="text-blue-300 text-xs">
                  The audit runs in the background and you will be notified when it completes.
                  An AI analysis will be automatically generated with remediation recommendations.
                </p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mt-4 bg-red-900/20 border border-red-500/30 rounded-lg p-3 flex items-center gap-2 max-w-2xl mx-auto">
              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <span className="text-red-300 text-sm">{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-5 border-t border-gray-800 flex-shrink-0">
          <button
            onClick={step === 1 ? onClose : () => setStep(s => s - 1)}
            className="flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            {step === 1 ? 'Annuler' : 'Retour'}
          </button>

          {step < 4 ? (
            <button
              onClick={handleNext}
              className="flex items-center gap-2 bg-brand-green text-brand-black px-6 py-2 rounded-lg font-bold hover:bg-green-500 transition-colors"
            >
              Suivant
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleLaunch}
              disabled={loading}
              className="flex items-center gap-2 bg-brand-green text-brand-black px-6 py-2 rounded-lg font-bold hover:bg-green-500 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Lancement...
                </>
              ) : (
                <>
                  <Cloud className="w-4 h-4" />
                  Lancer l'audit
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CloudScanWizard;
